import {
  OptionType,
  OptionValue,
  Option,
  OptionValueAttrs,
  OptionCreatePayload,
  OptionUpdatePayload,
  Translation,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { getDefaultValueIndex } from 'apis/restaurant-api/merchant-admin/menu2/utils';
import { VatCategory } from 'apis/vat-categories/types';
import {
  numberToNumberInputString,
  numberInputStringToNumber,
  monetaryAmountToNumberInputString,
  numberInputStringToDecimalString,
} from 'common/formatting/numberInput';

import { initialVatFieldValue } from '../components/VatCategories';
import { FormOptionValue, OptionFormData } from '../types';
import { mergeTranslations } from '../utils';

export const formOptionValueToOptionValueAttrs =
  (primaryLanguage: Language, currency: Currency, type: OptionType) =>
  (val: FormOptionValue): OptionValueAttrs => {
    const multiChoiceConfig =
      type === 'multi_choice'
        ? {
            selection_range: {
              min: numberInputStringToNumber(val.minimum_selections),
              max: numberInputStringToNumber(val.maximum_selections),
            },
          }
        : null;

    return {
      additives: val.additives ? [{ lang: primaryLanguage, value: val.additives, verified: true }] : [],
      allergens: val.allergens ? [{ lang: primaryLanguage, value: val.allergens, verified: true }] : [],
      availability_period: { start: null, end: null },
      deposit_permutation_id: val.deposit_permutation_id ? { $oid: val.deposit_permutation_id } : null,
      dietary_preferences: val.dietaryPreferences || [],
      distributor_information: val.distributorInformation
        ? [{ lang: primaryLanguage, value: val.distributorInformation, verified: true }]
        : [],
      enabled: !val.disabled,
      external_id: val.external_id || null,
      ingredients: val.ingredients ? [{ lang: primaryLanguage, value: val.ingredients, verified: true }] : [],
      mandatory_warnings: val.mandatoryWarnings,
      multi_choice_config: multiChoiceConfig,
      name: val.name ? [{ lang: primaryLanguage, value: val.name, verified: true }] : [],
      nutrition_facts: val.nutritionFacts ? [{ lang: primaryLanguage, value: val.nutritionFacts, verified: true }] : [],
      price: { amount: numberInputStringToDecimalString(val.price), currency },
      producer_information: val.producerInformation
        ? [{ lang: primaryLanguage, value: val.producerInformation, verified: true }]
        : [],
      vat_category_code: (val.vat?.type === 'categories' && val.vat.categoryCode) || null,
      vat_percentage: val.vat && val.vat.percentage !== undefined ? String(val.vat.percentage) : null,
      visible_to_courier: val.visible_to_courier,
      visible_to_merchant_app: type === 'choice' ? !val.hide_when_selected : true,
    };
  };

export const buildFormOptionValue = (
  primaryLanguage: Language,
  optionValue: OptionValue,
  vatCategories?: VatCategory[],
): FormOptionValue => {
  const nameTranslation = optionValue.name.find(t => t.lang === primaryLanguage);
  return {
    deposit_permutation_id: optionValue.deposit_permutation_id?.$oid || '',
    dietaryPreferences: optionValue.dietary_preferences,
    disabled: !optionValue.enabled,
    external_id: optionValue.external_id || '',
    hide_when_selected: !optionValue.visible_to_merchant_app,
    id: optionValue.id,
    mandatoryWarnings: optionValue.mandatory_warnings,
    maximum_selections:
      !optionValue.multi_choice_config || optionValue.multi_choice_config.selection_range.max === 0
        ? '' // TODO: These shouldn't be visible in the form when the option is not multi choice
        : numberToNumberInputString(optionValue.multi_choice_config.selection_range.max),
    minimum_selections: optionValue.multi_choice_config
      ? numberToNumberInputString(optionValue.multi_choice_config.selection_range.min)
      : '', // TODO: These shouldn't be visible in the form when the option is not multi choice
    name: nameTranslation ? nameTranslation.value : '',
    price: monetaryAmountToNumberInputString(optionValue.price),
    visible_to_courier: optionValue.visible_to_courier,
    ingredients: optionValue.ingredients.find(t => t.lang === primaryLanguage)?.value || '',
    additives: optionValue.additives.find(t => t.lang === primaryLanguage)?.value || '',
    nutritionFacts: optionValue.nutrition_facts.find(t => t.lang === primaryLanguage)?.value || '',
    allergens: optionValue.allergens.find(t => t.lang === primaryLanguage)?.value || '',
    producerInformation: optionValue.producer_information.find(t => t.lang === primaryLanguage)?.value || '',
    distributorInformation: optionValue.distributor_information.find(t => t.lang === primaryLanguage)?.value || '',
    vat: initialVatFieldValue(
      vatCategories,
      optionValue.vat_category_code || undefined,
      optionValue.vat_percentage ? Number(optionValue.vat_percentage) : undefined,
    ),
  };
};

const hex = (value: number) => {
  return Math.floor(value).toString(16);
};

export const objectId = () => {
  return hex(Date.now() / 1000) + ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16));
};

export const buildNewFormOptionValue = (overrides?: Partial<FormOptionValue>): FormOptionValue => ({
  additives: '',
  allergens: '',
  deposit_permutation_id: '',
  dietaryPreferences: [],
  disabled: false,
  distributorInformation: '',
  external_id: '',
  hide_when_selected: false,
  id: objectId(),
  ingredients: '',
  mandatoryWarnings: [],
  maximum_selections: '',
  minimum_selections: '0',
  name: '',
  nutritionFacts: '',
  price: '0',
  producerInformation: '',
  visible_to_courier: false,
  vat: { type: 'categories', percentage: undefined, categoryCode: undefined },
  ...overrides,
});

export const buildOptionBoolValuePlaceholders = (): FormOptionValue[] => [
  buildNewFormOptionValue({ price: '0' }),
  buildNewFormOptionValue({ price: '1' }),
];

export const buildCreateOptionFormData = (primaryLanguage: Language, fromOption?: Option): OptionFormData => {
  const getNamePrimaryLanguageValue = (name: Translation[]) => {
    const nameTranslation = name.find(t => t.lang === primaryLanguage);
    return nameTranslation ? nameTranslation.value : '';
  };

  const buildFormOptionValueCopy = (optionValue: OptionValue): FormOptionValue => ({
    ...buildFormOptionValue(primaryLanguage, optionValue),
    id: objectId(),
  });

  return fromOption
    ? {
        name: getNamePrimaryLanguageValue(fromOption.name),
        type: fromOption.type,
        defaultvalue_index: numberToNumberInputString(getDefaultValueIndex(fromOption)),
        values:
          fromOption.type === 'bool' ? [buildNewFormOptionValue()] : fromOption.values.map(buildFormOptionValueCopy),
        boolValues:
          fromOption.type === 'bool'
            ? fromOption.values.map(buildFormOptionValueCopy)
            : buildOptionBoolValuePlaceholders(),
        external_id: fromOption.external_id || '',
        visibleToCourier: fromOption.values.some(value => value.visible_to_courier),
      }
    : {
        name: '',
        values: [buildNewFormOptionValue()],
        type: 'choice',
        boolValues: buildOptionBoolValuePlaceholders(),
        defaultvalue_index: '0',
        external_id: '',
        visibleToCourier: false,
      };
};

export const buildUpdateOptionFormData = (primaryLanguage: Language, option: Option): OptionFormData => {
  const nameTranslation = option.name.find(t => t.lang === primaryLanguage);

  return {
    name: nameTranslation ? nameTranslation.value : '',
    type: option.type,
    defaultvalue_index: numberToNumberInputString(getDefaultValueIndex(option)),
    values:
      option.type === 'bool'
        ? [buildNewFormOptionValue()]
        : option.values.map(optionValue => buildFormOptionValue(primaryLanguage, optionValue)),
    boolValues:
      option.type === 'bool'
        ? option.values.map(optionValue => buildFormOptionValue(primaryLanguage, optionValue))
        : buildOptionBoolValuePlaceholders(),
    external_id: option.external_id || '',
    visibleToCourier: option.values.some(value => value.visible_to_courier),
  };
};

const sanitizeFormOptionValue = (optionValueFormData: FormOptionValue): FormOptionValue => {
  return {
    ...optionValueFormData,
    price: optionValueFormData.price.trim(),
  };
};

export const sanitizeOptionFormData = (formData: OptionFormData): OptionFormData => {
  return {
    ...formData,
    values: formData.values.map(sanitizeFormOptionValue),
    boolValues: formData.boolValues.map(sanitizeFormOptionValue),
  };
};

export const buildOptionCreatePayload = (
  primaryLanguage: Language,
  currency: Currency,
  menuId: string,
  formData: OptionFormData,
): OptionCreatePayload => {
  const toOptionValueAttrs = formOptionValueToOptionValueAttrs(primaryLanguage, currency, formData.type);
  return {
    menu_id: menuId,
    type: formData.type,
    name: formData.name ? [{ lang: primaryLanguage, value: formData.name, verified: true }] : [],
    external_id: formData.external_id || null,
    default_value_index: numberInputStringToNumber(formData.defaultvalue_index),
    default_value: null,
    values: (formData.type === 'bool' ? formData.boolValues : formData.values).map(value => {
      return {
        ...toOptionValueAttrs(value),
        visible_to_courier: formData.visibleToCourier,
      };
    }),
  };
};

export const buildOptionUpdatePayload = (
  primaryLanguage: Language,
  currency: Currency,
  menuId: string,
  option: Option,
  formData: OptionFormData,
): OptionUpdatePayload => {
  const toOptionValueAttrs = formOptionValueToOptionValueAttrs(primaryLanguage, currency, formData.type);
  const name = formData.name
    ? mergeTranslations({ lang: primaryLanguage, value: formData.name, verified: true }, option.name)
    : [];
  return {
    id: option.id,
    menu_id: menuId,
    type: formData.type,
    name,
    external_id: formData.external_id || null,
    default_value_index: numberInputStringToNumber(formData.defaultvalue_index),
    default_value: null,
    values: (formData.type === 'bool' ? formData.boolValues : formData.values).map(formOptionValue => {
      const attrs = { ...toOptionValueAttrs(formOptionValue), visible_to_courier: formData.visibleToCourier };
      const optionValue = option.values.find(v => v.id === formOptionValue.id);
      if (optionValue !== undefined) {
        attrs.name = mergeTranslations(attrs.name[0] || null, optionValue.name);
        attrs.additives = mergeTranslations(attrs.additives[0] || null, optionValue.additives);
        attrs.allergens = mergeTranslations(attrs.allergens[0] || null, optionValue.allergens);
        attrs.distributor_information = mergeTranslations(
          attrs.distributor_information[0] || null,
          optionValue.distributor_information,
        );
        attrs.ingredients = mergeTranslations(attrs.ingredients[0] || null, optionValue.ingredients);
        attrs.nutrition_facts = mergeTranslations(attrs.nutrition_facts[0] || null, optionValue.nutrition_facts);
        attrs.producer_information = mergeTranslations(
          attrs.producer_information[0] || null,
          optionValue.producer_information,
        );
        return { ...attrs, id: formOptionValue.id };
      }
      return attrs;
    }),
    version: option.version,
  };
};
